import type {BaseItemLayout, CompRef, GridTrackSize, LayoutSize} from '@wix/document-services-types'
import type {CalculateParams, ConverterArguments} from '../types'
import {getBottomMarginInPx, getLayout, getMax, getMin, getTopMarginInPx, getValue, isSection} from '../utils'
import type {SlotsExtensionAPI} from '../../../slots'
import {convertHeightToPx, getDefaultHeight} from './height'
import {isEmpty} from 'lodash'
import type {RMApi} from '../../../rendererModel'
import {convertWidthToPx} from './width'

const getHeightFromInternalContainer = (
    internalContainerRef: CompRef,
    converterArguments: ConverterArguments
): number => {
    return autoHeight({
        ...converterArguments,
        compRef: internalContainerRef,
        layoutData: getLayout(converterArguments.extensionAPI, internalContainerRef)
    })
}

const getHeightFromChildren = (
    children: CompRef[],
    {measurementsMapAPI, calculate, pointers, extensionAPI}: CalculateParams
): number => {
    const {get} = measurementsMapAPI
    let lowestChildBottomLine = 0
    let bottomMargin = 0
    children?.forEach(childRef => {
        const childLayoutData = getLayout(extensionAPI, childRef)
        const type = childLayoutData.componentLayout?.height?.type
        if (!type || type === 'percentage') {
            return
        }

        const calculateParams = {
            measurementsMapAPI,
            calculate,
            compRef: childRef,
            layoutData: childLayoutData,
            pointers,
            extensionAPI
        }

        calculate.height(calculateParams)

        const {margins} = childLayoutData.itemLayout as BaseItemLayout<string>
        const topMargin = getTopMarginInPx(calculateParams, convertWidthToPx, margins)
        const height = get.height(childRef)

        if (topMargin + height > lowestChildBottomLine) {
            lowestChildBottomLine = topMargin + height
            bottomMargin = getBottomMarginInPx(calculateParams, convertWidthToPx, margins)
        }
    })
    return lowestChildBottomLine + bottomMargin
}

const getHeightFromRows = (converterArguments: ConverterArguments, rows: GridTrackSize[], rowGap?: LayoutSize) => {
    const rowsHeight = rows.reduce((acc, row) => {
        return (
            acc +
            convertHeightToPx[row.type]({
                ...converterArguments,
                value: getValue(row),
                min: getMin(row),
                max: getMax(row)
            })
        )
    }, 0)

    let rowGapsHeight = 0
    if (rowGap) {
        const rowGapHeight = convertHeightToPx[rowGap.type]({
            ...converterArguments,
            value: getValue(rowGap),
            min: getMin(rowGap),
            max: getMax(rowGap)
        })
        rowGapsHeight = rowGapHeight * (rows.length - 1)
    }

    return rowsHeight + rowGapsHeight
}

export function autoHeight(converterArguments: ConverterArguments): number {
    const {pointers, compRef, extensionAPI, layoutData} = converterArguments
    const {rendererModel, slots} = extensionAPI as SlotsExtensionAPI & RMApi

    const children = pointers?.structure.getChildren(compRef) as CompRef[]
    if (children && !isEmpty(children)) {
        const hasInternalContainer =
            rendererModel.isBuilderComponentModel() &&
            (slots.is.unifiedContainerPlugin(children[0]) || isSection(extensionAPI, compRef))
        if (hasInternalContainer) {
            return getHeightFromInternalContainer(children[0], converterArguments)
        }
        const heightFromChildren = getHeightFromChildren(children, converterArguments)
        if (heightFromChildren) return heightFromChildren
    }

    const {containerLayout} = layoutData
    const rows = containerLayout && 'rows' in containerLayout ? containerLayout.rows : undefined
    const rowGap = containerLayout && 'rowGap' in containerLayout ? containerLayout.rowGap : undefined
    if (rows) {
        return getHeightFromRows(converterArguments, rows, rowGap)
    }

    return getDefaultHeight(converterArguments)
}
